import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { upload } from "../../utils/media-utils";
import { useRole } from "../room/useRole";
import { MAX_MESSAGE_LENGTH } from "../../utils/chat-message";
import { changeHub } from "../../change-hub";

export const ChatContext = createContext({ messageGroups: [], sendMessage: () => {} });

let uniqueMessageId = 0;

const NEW_MESSAGE_GROUP_TIMEOUT = 1000 * 60;

function shouldCreateNewMessageGroup(messageGroups, newMessage, now) {
  if (messageGroups.length === 0) {
    return true;
  }

  const lastMessageGroup = messageGroups[messageGroups.length - 1];

  if (lastMessageGroup.senderSessionId !== newMessage.sessionId) {
    return true;
  }
  if (lastMessageGroup.type !== newMessage.type) {
    return true;
  }

  const lastMessage = lastMessageGroup.messages[lastMessageGroup.messages.length - 1];

  return now - lastMessage.timestamp > NEW_MESSAGE_GROUP_TIMEOUT;
}

function processChatMessage(messageGroups, newMessage, mySessionId) {
  const now = Date.now();
  const { name, sent, sessionId } = newMessage;
  let { ...messageProps } = newMessage;

  if (shouldCreateNewMessageGroup(messageGroups, newMessage, now)) {
    return [
      ...messageGroups,
      {
        id: uniqueMessageId++,
        timestamp: now,
        sent,
        sender: mySessionId === sessionId ? "You" : name,
        senderSessionId: sessionId,
        messages: [{ id: uniqueMessageId++, timestamp: now, ...messageProps }],
        type: newMessage.type
      }
    ];
  }

  const lastMessageGroup = messageGroups.pop();
  lastMessageGroup.messages = [
    ...lastMessageGroup.messages,
    { id: uniqueMessageId++, timestamp: now, ...messageProps }
  ];

  return [...messageGroups, { ...lastMessageGroup }];
}

// Returns the new message groups array when we receive a message.
// If the message is ignored, we return the original message group array.
function updateMessageGroups(messageGroups, newMessage, mySessionId) {
  switch (newMessage.type) {
    case "change_hub":
      changeHub(
        newMessage.body.hubId,
        newMessage.body.addToHistory,
        newMessage.body.waypoint,
        newMessage.body.smoothTransition
      );

      return messageGroups;
    case "join":
    case "entered":
    case "leave":
    case "display_name_changed":
    case "scene_changed":
    case "hub_name_changed":
    case "hub_changed":
    case "log":
      return [
        ...messageGroups,
        {
          id: uniqueMessageId++,
          systemMessage: true,
          timestamp: Date.now(),
          ...newMessage
        }
      ];
    case "chat":
    case "image":
    case "photo":
    case "video":
    case "permission":
    case "spawn":
    case "audio":
    case "object":
      return processChatMessage(messageGroups, newMessage, mySessionId);
    default:
      return messageGroups;
  }
}

export function ChatContextProvider({ messageDispatch, children, mySessionId }) {
  const [messageGroups, setMessageGroups] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(false);
  const isMod = useRole("owner");

  useEffect(() => {
    function onReceiveMessage(event) {
      const newMessage = event.detail;

      if (
        (isMod && newMessage.sessionId === NAF.clientId && newMessage.type === "permission") ||
        newMessage?.body?.privacyType
      )
        return;

      setMessageGroups(messages => updateMessageGroups(messages, newMessage, mySessionId));

      if (
        newMessage.type === "chat" ||
        newMessage.type === "image" ||
        newMessage.type === "photo" ||
        newMessage.type === "video" ||
        newMessage.type === "audio" ||
        newMessage.type === "object" ||
        newMessage.type === "permission"
      ) {
        setUnreadMessages(true);
      }
    }

    if (messageDispatch) {
      messageDispatch.addEventListener("message", onReceiveMessage);
    }

    return () => {
      if (messageDispatch) {
        messageDispatch.removeEventListener("message", onReceiveMessage);
      }
    };
  }, [messageDispatch, setMessageGroups, setUnreadMessages, isMod, mySessionId]);

  const sendMessage = useCallback(
    message => {
      if (messageDispatch) {
        messageDispatch.dispatch(message);
      }
    },
    [messageDispatch]
  );

  const sendMessageSpawn = useCallback(
    async (src, file) => {
      if (messageDispatch) {
        const response = await upload(file, file?.type);

        window.APP.store.update({
          uploadPromotionTokens: [{ fileId: response.file_id, promotionToken: response.meta.promotion_token }]
        });
        const url = response.origin + `?token=${response.meta.access_token}`;
        if (file?.type?.startsWith("image/")) {
          messageDispatch.push({ src: url, name: file.name, canSpawn: true }, "image");
        } else if (file?.type?.startsWith("video/")) {
          messageDispatch.push({ src: url, name: file.name, canSpawn: true }, "video");
        } else if (file?.type?.startsWith("audio/")) {
          messageDispatch.push({ src: url, name: file.name, canSpawn: true }, "audio");
        } else {
          messageDispatch.push({ src: url, name: file.name, canSpawn: true }, "object");
        }
      }
    },
    [messageDispatch]
  );

  const setMessagesRead = useCallback(() => {
    setUnreadMessages(false);
  }, [setUnreadMessages]);

  return (
    <ChatContext.Provider value={{ messageGroups, unreadMessages, sendMessage, setMessagesRead, sendMessageSpawn }}>
      {children}
    </ChatContext.Provider>
  );
}

ChatContextProvider.propTypes = {
  children: PropTypes.node,
  messageDispatch: PropTypes.object
};

export const useChat = ({
  scene,
  canTextChat,
  sendMessage,
  message,
  setMessage,
  sendMessageSpawn,
  isCommand,
  setIsCommand,
  typingTimeoutRef,
  inputRef
}) => {
  const onKeyDown = useCallback(
    e => {
      setIsCommand(e.target.value.startsWith("/"));
      if (!canTextChat && !isCommand) return;
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
          sendMessage(e.target.value);
          setMessage("");
          // intentionally only doing this on "enter" press and not clicking of send button
          // if (e.target.value.startsWith("/")) {
          //   onClose();
          // }
        }
      } else if (e.key === "Escape") {
        // onClose();
      }

      // [TODO FIX] THIS IS CAUSING ALL UI TO BE RENDERED
      // clearTimeout(typingTimeoutRef.current);
      // typingTimeoutRef.current = setTimeout(() => window.APP.hubChannel.endTyping(), 500);
      // window.APP.hubChannel.beginTyping();
    },
    [setIsCommand, canTextChat, isCommand, sendMessage, setMessage]
  );

  const onUploadAttachments = useCallback(
    e => {
      // TODO: Right now there's no way to upload files to the chat only.
      // When we add the place menu whcih will have an explicit button for uploading files,
      // should we make this attach button only upload to chat?
      for (const file of e.target.files) {
        // scene.emit("add_media", { src: file });
        const src = URL.createObjectURL(file);

        sendMessageSpawn(src, file);
      }
    },
    [sendMessageSpawn]
  );

  const onSendMessage = useCallback(() => {
    sendMessage(message.substring(0, MAX_MESSAGE_LENGTH));
    setMessage("");
  }, [message, sendMessage, setMessage]);

  const onSpawn = useCallback(src => {
    window.APP.scene.emit("add_media", { src });
  }, []);

  const isOverMaxLength = message.length > MAX_MESSAGE_LENGTH;
  const isDisabled = message.length === 0 || isOverMaxLength || !canTextChat;

  const onSelectEmoji = useCallback(
    ({ emoji, pickerRemainedOpen }) => {
      setMessage(message => message + emoji.native);
      // If the picker remained open, avoid selecting the input so that the
      // user can keep picking emojis.
      if (!pickerRemainedOpen) inputRef.current.select();
    },
    [setMessage, inputRef]
  );

  return {
    onKeyDown,
    onUploadAttachments,
    onSendMessage,
    onSpawn,
    isOverMaxLength,
    isDisabled,
    onSelectEmoji
  };
};
